import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import ArtistCard from '@/components/ArtistCard';
import AISearchBar from '@/components/AISearchBar';
import { Loader2, Sparkles, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const Artists = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [skill, setSkill] = useState(searchParams.get('skill') || '');
  const [budgetMin, setBudgetMin] = useState(searchParams.get('budget_min') || '');
  const [budgetMax, setBudgetMax] = useState(searchParams.get('budget_max') || '');
  const [availableOn, setAvailableOn] = useState(searchParams.get('available_on') || '');

  // Fetch whenever the URL search params change (AI query or manual filters)
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const ai = searchParams.get('ai');
        if (ai) {
          const { data } = await api.post('/ai/search', { query: ai });
          setArtists(data.artists);
          setAiSummary({ query: ai, filters: data.filters });
        } else {
          const params = {};
          ['q', 'category', 'city', 'skill', 'budget_min', 'budget_max', 'available_on'].forEach((k) => {
            const v = searchParams.get(k);
            if (v) params[k] = v;
          });
          const { data } = await api.get('/artists', { params });
          setArtists(data.artists);
          setAiSummary(null);
        }
      } finally { setLoading(false); }
    };
    run();
  }, [searchParams]);

  const handleAIResult = (data) => {
    setArtists(data.artists);
    setAiSummary({ query: 'AI Search', filters: data.filters });
  };

  const apply = () => {
    const next = {};
    if (q) next.q = q;
    if (category) next.category = category;
    if (city) next.city = city;
    if (skill) next.skill = skill;
    if (budgetMin) next.budget_min = budgetMin;
    if (budgetMax) next.budget_max = budgetMax;
    if (availableOn) next.available_on = availableOn;
    setSearchParams(next);
  };

  const clear = () => {
    setQ(''); setCategory(''); setCity(''); setSkill(''); setBudgetMin(''); setBudgetMax(''); setAvailableOn('');
    setSearchParams({});
  };

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6 sm:px-12">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-3">Discover</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
          The <span className="rainbow-text">talent</span> universe
        </h1>
        <p className="text-slate-400 max-w-2xl mx-auto">Search 12,000+ verified artists. Ask in plain English, or filter manually.</p>
      </div>

      <div className="mb-10">
        <AISearchBar onResult={handleAIResult} large={false} />
      </div>

      {aiSummary && (
        <div className="glass-strong rounded-2xl px-5 py-4 mb-8 flex items-center gap-3 text-sm text-slate-200">
          <Sparkles className="h-4 w-4 text-fuchsia-300 shrink-0" />
          <span className="text-slate-300">
            AI parsed: <span className="text-white">{aiSummary.filters?.intent_summary || aiSummary.query}</span>
            {aiSummary.filters?.category && <> · <span className="text-fuchsia-300">{aiSummary.filters.category}</span></>}
            {aiSummary.filters?.city && <> · <span className="text-cyan-300">{aiSummary.filters.city}</span></>}
            {aiSummary.filters?.budget_max_inr && <> · <span className="text-amber-300">≤ ₹{aiSummary.filters.budget_max_inr.toLocaleString('en-IN')}</span></>}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-10">
        <Filter className="h-4 w-4 text-slate-400 mb-3" />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Keyword</label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name / bio" data-testid="artists-filter-q-input" className="bg-white/5 border-white/10 max-w-[200px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Category</label>
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Singer, DJ…" data-testid="artists-filter-category-input" className="bg-white/5 border-white/10 max-w-[180px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">City</label>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Mumbai…" data-testid="artists-filter-city-input" className="bg-white/5 border-white/10 max-w-[160px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Skill</label>
          <Input value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="Bharatanatyam…" data-testid="artists-filter-skill-input" className="bg-white/5 border-white/10 max-w-[200px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Budget min (₹)</label>
          <Input type="number" min="0" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} placeholder="50000" data-testid="artists-filter-budget-min-input" className="bg-white/5 border-white/10 max-w-[140px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Budget max (₹)</label>
          <Input type="number" min="0" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} placeholder="500000" data-testid="artists-filter-budget-max-input" className="bg-white/5 border-white/10 max-w-[140px] mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Available on</label>
          <Input type="date" value={availableOn} onChange={(e) => setAvailableOn(e.target.value)} data-testid="artists-filter-date-input" className="bg-white/5 border-white/10 max-w-[170px] mt-1" />
        </div>
        <Button onClick={apply} data-testid="artists-filter-apply-button" className="btn-ai text-white border-0 rounded-full px-5">Apply</Button>
        <Button onClick={clear} variant="outline" data-testid="artists-filter-clear-button" className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5 rounded-full px-5">Clear</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>
      ) : artists.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No artists found. Try a different search.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {artists.map((a) => <ArtistCard key={a.artist_id} artist={a} showMatch={!!aiSummary} />)}
        </div>
      )}
    </div>
  );
};

export default Artists;
